import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import baseUrl from '../BaseUrl';
import CeoRequestsFilters from './CeoRequestsFilters';
import CeoRequestsSummaryPanel from './CeoRequestsSummaryPanel';
import CeoRequestsTable from './CeoRequestsTable';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import '../active-users/active-users.css';
import './ceo-requests.css';

const PAGE_SIZE = 20;
const FILTER_KEY = 'ceoRequestsState';
const EMPTY_FILTERS = { search: '', from: '', to: '' };

/**
 * CEO Requests (066) — the TAB BODY.
 *
 * Extracted from the standalone page in 067 so the four feedback sources could live as tabs on
 * one screen. The portal shell (panelbox / Navbar / Header / content-page) and the breadcrumb bar
 * now belong to components/user-feedback/UserFeedback.js; everything below the heading — state,
 * filters, summary, table, paging, export — moved here VERBATIM.
 *
 * Nothing about this source's behaviour changed. Its existing test suite is the proof: it had to
 * pass untouched, and it did.
 */
export default function CeoRequestsBody() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(() => readSavedFilters(FILTER_KEY) || EMPTY_FILTERS);
  const [summary, setSummary] = useState(null);
  const [notice, setNotice] = useState('');

  usePersistFilters(FILTER_KEY, filters);

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== '' && v !== null && v !== undefined
  );

  const rangeStart = totalItem === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItem);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      if (expire * 1000 < new Date().getTime()) {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); }
    }
  };

  useEffect(() => {
    if (token) { loadList(page, filters); loadSummary(filters); }
    /* eslint-disable-next-line */
  }, [token]);

  const loadList = async (toPage, f) => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/ceo-requests`, {
        params: { ...f, page: toPage, limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (res.data && res.data.data) || {};
      setItems(data.requests || []);
      setTotalItem((data.pagination && data.pagination.total) || 0);
      setPage(toPage);
    } catch (e) {
      // Deliberately NOT an empty list: "we could not reach Firestore" and "no member has
      // written" must never look the same to an admin (FR-050).
      setError('Failed to load CEO requests.');
      setItems([]);
      setTotalItem(0);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async (f) => {
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/ceo-requests/summary`, {
        params: f,
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary((res.data && res.data.data) || null);
    } catch (e) {
      // Supporting detail only — its failure must not blank the list of requests.
      setSummary(null);
    }
  };

  const handleApply = (f) => { setFilters(f); loadList(1, f); loadSummary(f); };
  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    loadList(1, EMPTY_FILTERS);
    loadSummary(EMPTY_FILTERS);
  };

  const handleExport = async () => {
    setNotice('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/ceo-requests/export`, {
        params: filters,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Truncation is never silent — say so before the file opens (FR-041).
      if (res.headers && res.headers['x-export-truncated'] === 'true') {
        setNotice('This export hit the 50,000 row cap. Narrow the filters to get the rest.');
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'ceo-requests.csv');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setNotice('Export failed. Please try again.');
    }
  };

  return (
    <>
      <h2 className="au-title uf-tab-title cr-title">CEO Requests</h2>
      <p className="cr-subtitle">
        {loading ? 'Loading requests…' : `${totalItem} request${totalItem === 1 ? '' : 's'}`}
        {!loading && totalItem > 0 && (
          <span className="cr-range"> · showing {rangeStart}–{rangeEnd}</span>
        )}
      </p>

      <CeoRequestsFilters
        values={filters}
        onApply={handleApply}
        onClear={handleClear}
        onExport={handleExport}
      />

      {notice && <div className="cr-notice" role="alert">{notice}</div>}

      <CeoRequestsSummaryPanel summary={summary} />

      {loading ? (
        <TableSkeleton rows={8} />
      ) : error ? (
        <ErrorState message={error} onRetry={() => loadList(page, filters)} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No CEO requests yet"
          message="No member has written to the CEO from the app for the current filters."
          hasFilters={hasActiveFilters}
          onClear={handleClear}
        />
      ) : (
        <>
          <CeoRequestsTable items={items} />
          {totalItem > PAGE_SIZE && (
            <div className="cr-pagination">
              <Pagination
                activePage={page}
                itemsCountPerPage={PAGE_SIZE}
                totalItemsCount={totalItem}
                pageRangeDisplayed={5}
                onChange={(p) => loadList(p, filters)}
                itemClass="page-item"
                linkClass="page-link"
              />
            </div>
          )}
        </>
      )}
    </>
  );
}
